import { createPortal } from 'react-dom'

import { useDisplayScale } from '../layouts/useDisplayScale.js'
import { useSound } from '../sound/sound-context.js'
import { KeyCap } from './KeyCap.js'
import { useLegendSlot } from './legend-slot.js'

export interface Shortcut {
  /** The keys themselves, e.g. ['↑', '↓'] or ['Esc']. */
  readonly keys: readonly string[]
  /** What pressing them does, in the fewest words that are still a sentence. */
  readonly does: string
}

/**
 * The keys, along the bottom, drawn as keys.
 *
 * A keyboard-only player who cannot guess the keys is stuck with no way to
 * ask, and a child will not think to press "?". Prose hints said the same
 * thing and read as instructions to be obeyed; a legend reads as a map, which
 * is what somebody glancing down mid-game actually wants.
 *
 * Hidden where there is a coarse pointer, since a phone has no keys to press —
 * deliberately not gated on `pointer: fine`, because a kiosk with nothing but
 * a keyboard attached is the textbook case for `pointer: none` and is exactly
 * who this is for.
 *
 * On the television it is drawn into a slot at the bottom of the layout —
 * full width, below the game rather than inside its column, at the body size
 * rather than the small one. Two reasons, and neither is decoration. It is the
 * only thing telling somebody with a Bluetooth keyboard and no mouse what they
 * can press, and at 1.5rem across a room it was a footnote to the game. And
 * belonging to the screen rather than to the game means it is in the same
 * place every time.
 *
 * A slot, not `position: fixed` with a reserved height. That was the first
 * attempt: the height was a constant, Small Rabbit's Scramble has six
 * shortcuts, they wrapped to two rows on a 16:9 screen, and the second row was
 * drawn past the bottom of the display where no amount of scrolling could
 * reach it. Silently — a fixed box clips its overflow off the edge of the
 * world rather than pushing anything. A slot takes the height its contents
 * need and the game gets the rest.
 */
/**
 * "A to Z", when the keys are written as a range.
 *
 * Null for anything else, so an ordinary list of keys is read out as the keys
 * it is rather than being narrated.
 */
function rangeOf(keys: readonly string[]): string | null {
  if (keys.length !== 3 || !/^[–—-]$/.test(keys[1] ?? '')) return null

  return `${keys[0] ?? ''} to ${keys[2] ?? ''}`
}

export function KeyLegend({ shortcuts }: { shortcuts: readonly Shortcut[] }) {
  const slot = useLegendSlot()
  const scale = useDisplayScale()
  const sound = useSound()

  /*
   * The mute key, added here rather than by each screen.
   *
   * It works on all of them — see `SoundProvider` for why it is `#` — so
   * every legend has to offer it, and seven copies of that is seven chances
   * for one to say the wrong thing or to be forgotten on the next game. Added
   * where the offer is drawn, so the key and the legend cannot come apart.
   *
   * Only where anything can be heard, which is only the television.
   *
   * Two words, not four. "Turn the sound off" pushed Rocky's legend from one
   * row to two, which took 61px from the play area his clue sizes were
   * measured against — and his are the tightest on the screen, so his riddles
   * stopped fitting. The legend is a shared budget: a longer word here is
   * height taken from every game.
   */
  const offered: readonly Shortcut[] = sound.heard
    ? [...shortcuts, { keys: ['#'], does: sound.on ? 'Sound off' : 'Sound on' }]
    : shortcuts

  const legend = (
    <ul
      aria-label="Keyboard shortcuts"
      className={[
        'border-border text-ink-muted mt-1 flex list-none flex-wrap items-center justify-center',
        'gap-x-5 gap-y-2 border-t p-0 pt-3 text-(length:--text-small)',
        '[@media(pointer:coarse)]:hidden',
        // Full width and larger, in the slot — television only. The keycap size
        // is set here rather than inside KeyCap, so the keys grow with the
        // words beside them without changing what a keycap is anywhere else.
        '[[data-scale=tv]_&]:bg-surface-raised [[data-scale=tv]_&]:m-0',
        '[[data-scale=tv]_&]:gap-x-10 [[data-scale=tv]_&]:px-8 [[data-scale=tv]_&]:py-4',
        '[[data-scale=tv]_&]:text-(length:--text-body) [[data-scale=tv]_&]:[--text-keycap:var(--text-body)]',
      ].join(' ')}
    >
      {offered.map((shortcut) => (
        <li key={shortcut.does} className="flex items-center gap-2">
          {/*
            A range says so out loud.
            
            Hiding the dash fixed the look and made the reading worse: the two
            keycaps collapse to "A Z", which is not a range and not anything
            else either. The label carries what the dash used to.
          */}
          <span
            className="flex items-center gap-1"
            aria-label={rangeOf(shortcut.keys) ?? undefined}
            role={rangeOf(shortcut.keys) ? 'text' : undefined}
          >
            {shortcut.keys.map((key) =>
              /*
               * A range is written ['A', '–', 'Z'], and the dash in the middle
               * is not a key anybody presses — drawn as one it read as three
               * keys, the middle of which was a hyphen.
               */
              /^[–—-]$/.test(key) ? (
                <span key={key} aria-hidden="true">
                  {key}
                </span>
              ) : (
                <KeyCap key={key}>{key}</KeyCap>
              ),
            )}
          </span>
          {shortcut.does}
        </li>
      ))}
    </ul>
  )

  // Only the television moves it. Everywhere else it stays where it is
  // written, at the bottom of the game's own column, which is right for a
  // laptop and invisible on a phone.
  return scale === 'tv' && slot ? createPortal(legend, slot) : legend
}
